/**
 * Dual-handle price range slider for the collection-page price filter.
 *
 * Drives the existing min/max text inputs rendered by snippets/price-filter.liquid.
 * The text inputs remain the source of truth; this slider mirrors them and
 * dispatches a bubbling `change` event so the surrounding
 * <price-facet-component on:change="/updatePriceFilterAndResults"> handler fires.
 */
class PriceRangeSlider extends HTMLElement {
  static MIN_GAP = 1; // Smallest distance (in display units) the two handles can be apart.
  static DEBOUNCE_MS = 300; // Wait after the last drag before applying the filter.

  connectedCallback() {
    this.minHandle = this.querySelector('.price-range-slider__handle--min');
    this.maxHandle = this.querySelector('.price-range-slider__handle--max');
    this.range = this.querySelector('.price-range-slider__range');

    if (!this.minHandle || !this.maxHandle || !this.range) return;

    this.min = Number(this.dataset.min ?? 0);
    this.max = Number(this.dataset.max ?? 0);
    this.minInputId = this.dataset.minInputId;
    this.maxInputId = this.dataset.maxInputId;

    this.minInput = this.minInputId ? document.getElementById(this.minInputId) : null;
    this.maxInput = this.maxInputId ? document.getElementById(this.maxInputId) : null;

    if (this.max <= this.min) {
      this.style.display = 'none';
      return;
    }

    this._abort = new AbortController();
    const { signal } = this._abort;

    this.minHandle.addEventListener('input', this.onMinHandleInput, { signal });
    this.maxHandle.addEventListener('input', this.onMaxHandleInput, { signal });
    this.minHandle.addEventListener('change', this.commit, { signal });
    this.maxHandle.addEventListener('change', this.commit, { signal });

    if (this.minInput) this.minInput.addEventListener('input', this.syncFromInputs, { signal });
    if (this.maxInput) this.maxInput.addEventListener('input', this.syncFromInputs, { signal });

    this.updateFill();
  }

  disconnectedCallback() {
    clearTimeout(this._debounceTimer);
    this._abort?.abort();
  }

  onMinHandleInput = () => {
    const minVal = Number(this.minHandle.value);
    const maxVal = Number(this.maxHandle.value);
    if (minVal > maxVal - PriceRangeSlider.MIN_GAP) {
      this.minHandle.value = String(Math.max(this.min, maxVal - PriceRangeSlider.MIN_GAP));
    }
    this.updateFill();
    this.scheduleCommit();
  };

  onMaxHandleInput = () => {
    const minVal = Number(this.minHandle.value);
    const maxVal = Number(this.maxHandle.value);
    if (maxVal < minVal + PriceRangeSlider.MIN_GAP) {
      this.maxHandle.value = String(Math.min(this.max, minVal + PriceRangeSlider.MIN_GAP));
    }
    this.updateFill();
    this.scheduleCommit();
  };

  syncFromInputs = () => {
    if (this.minInput) {
      const v = this.parseInputValue(this.minInput.value);
      if (!Number.isNaN(v)) this.minHandle.value = String(this.clamp(v));
    }
    if (this.maxInput) {
      const v = this.parseInputValue(this.maxInput.value);
      if (!Number.isNaN(v)) this.maxHandle.value = String(this.clamp(v));
    }
    this.updateFill();
  };

  /**
   * Parse user-typed price strings. Accepts "10", "10.50", "1,000.50" (en),
   * and "10,50" / "1.000,50" (eu).
   * @param {string} str
   * @returns {number}
   */
  parseInputValue(str) {
    if (!str) return NaN;
    const cleaned = String(str).trim();
    if (!cleaned) return NaN;
    // If both . and , appear, the last one is the decimal separator.
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    let normalized;
    if (lastDot !== -1 && lastComma !== -1) {
      const decimal = lastDot > lastComma ? '.' : ',';
      const thousand = decimal === '.' ? ',' : '.';
      normalized = cleaned.split(thousand).join('');
      if (decimal === ',') normalized = normalized.replace(',', '.');
    } else if (lastComma !== -1) {
      // Only commas — could be thousands ("1,000") or decimal ("10,50").
      // Heuristic: if exactly one comma and 1-2 digits follow it, treat as decimal.
      const after = cleaned.length - lastComma - 1;
      const commaCount = (cleaned.match(/,/g) || []).length;
      normalized = commaCount === 1 && after > 0 && after <= 2
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned;
    }
    return Number(normalized);
  }

  clamp(v) {
    return Math.max(this.min, Math.min(this.max, v));
  }

  updateFill() {
    const span = this.max - this.min;
    if (span <= 0) return;
    const minPct = ((Number(this.minHandle.value) - this.min) / span) * 100;
    const maxPct = ((Number(this.maxHandle.value) - this.min) / span) * 100;
    this.range.style.left = minPct + '%';
    this.range.style.width = Math.max(0, maxPct - minPct) + '%';
  }

  scheduleCommit = () => {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(this.commit, PriceRangeSlider.DEBOUNCE_MS);
  };

  commit = () => {
    clearTimeout(this._debounceTimer);
    const minVal = Number(this.minHandle.value);
    const maxVal = Number(this.maxHandle.value);

    if (this.minInput) {
      // Only set the min input when the user has actually moved the min handle off zero,
      // otherwise leave it blank so the filter URL stays clean.
      const newVal = minVal <= this.min ? '' : String(minVal);
      if (this.minInput.value !== newVal) {
        this.minInput.value = newVal;
        this.minInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (this.maxInput) {
      const newVal = maxVal >= this.max ? '' : String(maxVal);
      if (this.maxInput.value !== newVal) {
        this.maxInput.value = newVal;
        this.maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
